import { PLAYFIELD_PATTERNS, SOUND_PACKS, THEME_PRESETS, type PlayfieldPattern } from '../assets/catalog';
import { ART_PIECES, SOUND_EFFECTS } from '../assets/creatorPacks';
import type { MissionTrigger, TableDefinition, TablePart, TargetProp, Vec2 } from '../engine/tables/definition';

const MIN_WIDTH = 40;
const MAX_WIDTH = 100;
const MIN_HEIGHT = 80;
const MAX_HEIGHT = 200;
const MAX_PLUNGER_POWER = 200;
const MAX_BALL_SAVER_SECONDS = 60;
const MAX_NUDGE_COUNT = 10;
const MAX_TABLE_PARTS = 500;
const MAX_POINTS_PER_PART = 256;
const MAX_MISSIONS = 50;
const MAX_MISSION_COUNT = 1000;
const MAX_RULE_BONUS = 1_000_000_000;
const MAX_COMBO_WINDOW_MS = 60_000;
const MAX_BOOST_SECONDS = 60;
const MAX_BOOST_MULTIPLIER = 10;
const MAX_KICKER_HOLD_MS = 10_000;
const MAX_INSERT_EMISSIVE = 10;
const MAX_FLIPPER_SWEEP_DEG = 360;
const MAX_BUMPER_IMPULSE = 250;
const MAX_SCRIPTED_VELOCITY_COMPONENT = 250;
const MIN_RAMP_WIDTH = 3.6;
const MAX_RAMP_WIDTH = 9;
const RAMP_ENDPOINT_TOLERANCE = 0.001;
const THEME_IDS = new Set(THEME_PRESETS.map((item) => item.id));
const SOUND_IDS = new Set(SOUND_PACKS.map((item) => item.id));
const PATTERN_IDS = new Set(PLAYFIELD_PATTERNS.map((item) => item.id));
const SOUND_EFFECT_IDS = new Set(SOUND_EFFECTS.map((item) => item.id));
const ART_IDS = new Set(ART_PIECES.map((item) => item.id));
const TARGET_PROPS = new Set<TargetProp>(['none', 'stone-wall', 'ghost', 'robot', 'gargoyle', 'crystal']);
const MISSION_TRIGGERS = new Set<MissionTrigger>(['bumper', 'slingshot', 'target', 'rollover', 'ramp', 'kicker', 'prop-destroy']);
const PLAYABLE_PART_KINDS = new Set<TablePart['kind']>([
  'wall',
  'guide-rail',
  'post',
  'insert',
  'flipper-left',
  'flipper-right',
  'plunger-lane',
  'bumper',
  'slingshot',
  'rollover',
  'target',
  'ramp',
  'kicker',
  'drain',
]);

function pointsForPart(part: TablePart): Vec2[] {
  if ('points' in part) return part.points;
  if ('center' in part) return [part.center];
  if ('pivot' in part) return [part.pivot];
  if ('entry' in part) return [part.entry, part.exit, ...part.path];
  if (part.kind === 'plunger-lane') {
    const { x, y, width, height } = part.bounds;
    return [
      { x, y }, { x: x + width, y }, { x, y: y + height }, { x: x + width, y: y + height },
      ...(part.restPosition ? [part.restPosition] : []), ...(part.divider?.points ?? []),
      ...(part.stop ? [part.stop.center] : []), ...(part.launchPath ?? []),
    ];
  }
  return [];
}

function positiveFinite(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

function positiveFiniteAtMost(value: number, max: number): boolean {
  return positiveFinite(value) && value <= max;
}

function nonNegativeFinite(value: number, max = Number.POSITIVE_INFINITY): boolean {
  return Number.isFinite(value) && value >= 0 && value <= max;
}

function finiteMagnitudeAtMost(value: number, max: number): boolean {
  return Number.isFinite(value) && Math.abs(value) <= max;
}

function pointsApproximatelyEqual(a: Vec2, b: Vec2): boolean {
  return Number.isFinite(a.x)
    && Number.isFinite(a.y)
    && Number.isFinite(b.x)
    && Number.isFinite(b.y)
    && Math.abs(a.x - b.x) <= RAMP_ENDPOINT_TOLERANCE
    && Math.abs(a.y - b.y) <= RAMP_ENDPOINT_TOLERANCE;
}

function validOptionalBoost(seconds: number | undefined, multiplier: number | undefined): boolean {
  if (seconds !== undefined && !nonNegativeFinite(seconds, MAX_BOOST_SECONDS)) return false;
  if (multiplier !== undefined && (!Number.isFinite(multiplier) || multiplier < 1 || multiplier > MAX_BOOST_MULTIPLIER)) return false;
  return true;
}

function isRemoteOrEmbeddedUrl(value: string | undefined): boolean {
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return normalized.startsWith('http://') || normalized.startsWith('https://') || normalized.startsWith('data:') || normalized.startsWith('blob:') || normalized.startsWith('javascript:');
}

function missionPartMatchesTrigger(part: TablePart, trigger: MissionTrigger): boolean {
  if (trigger === 'prop-destroy') return part.kind === 'target' && Boolean(part.creatureRef && part.creatureRef !== 'none' && part.hitPoints);
  return part.kind === trigger;
}

function validateTable(table: TableDefinition, requirePlayableHardware: boolean): string[] {
  const errors: string[] = [];
  const { width, height } = table.board;
  const maxBoardSpan = Math.max(width, height);

  if (table.schemaVersion !== 1) errors.push('Unsupported table schema version.');
  if (!Number.isFinite(width) || width < MIN_WIDTH || width > MAX_WIDTH) errors.push('Board width must be 40–100 finite units.');
  if (!Number.isFinite(height) || height < MIN_HEIGHT || height > MAX_HEIGHT) errors.push('Board height must be 80–200 finite units.');
  if (!Number.isFinite(table.board.inclineDeg) || table.board.inclineDeg < 0 || table.board.inclineDeg > 15) errors.push('Board incline must be between 0° and 15°.');
  if (!Number.isFinite(table.board.plungerMaxPower) || table.board.plungerMaxPower < 1 || table.board.plungerMaxPower > MAX_PLUNGER_POWER) errors.push(`Plunger power must be between 1% and ${MAX_PLUNGER_POWER}%.`);
  if (!Number.isInteger(table.board.ballsPerGame) || table.board.ballsPerGame < 1 || table.board.ballsPerGame > 10) errors.push('Balls per game must be an integer from 1–10.');
  if (!nonNegativeFinite(table.board.ballSaverSeconds, MAX_BALL_SAVER_SECONDS)) errors.push(`Ball saver must be between 0 and ${MAX_BALL_SAVER_SECONDS} seconds.`);
  if (table.board.nudgeCount !== undefined && (!Number.isInteger(table.board.nudgeCount) || table.board.nudgeCount < 0 || table.board.nudgeCount > MAX_NUDGE_COUNT)) errors.push(`Nudges must be an integer from 0–${MAX_NUDGE_COUNT}.`);
  if (!table.name.trim()) errors.push('Table name is required.');
  if (!table.id.trim()) errors.push('Table id is required.');
  if (table.parts.length > MAX_TABLE_PARTS) {
    errors.push(`A table may define at most ${MAX_TABLE_PARTS} parts.`);
    return [...new Set(errors)];
  }

  if (table.theme.presetId && !THEME_IDS.has(table.theme.presetId)) errors.push('Unknown curated theme preset.');
  if (table.theme.visualPackId && !THEME_IDS.has(table.theme.visualPackId)) errors.push('Unknown curated visual pack.');
  if (table.theme.soundPackId && !SOUND_IDS.has(table.theme.soundPackId)) errors.push('Unknown curated sound pack.');
  if (table.theme.playfieldPattern && !PATTERN_IDS.has(table.theme.playfieldPattern as PlayfieldPattern)) errors.push('Unknown curated playfield pattern.');
  if (isRemoteOrEmbeddedUrl(table.theme.playfieldArt) || isRemoteOrEmbeddedUrl(table.theme.envMap)) errors.push('Published table art must use curated asset IDs, not arbitrary URLs or embedded data.');

  for (const effectId of Object.values(table.theme.soundEventMap ?? {})) {
    if (effectId && !SOUND_EFFECT_IDS.has(effectId as (typeof SOUND_EFFECTS)[number]['id'])) errors.push(`Unknown curated sound effect: ${effectId}.`);
  }
  for (const artId of Object.values(table.theme.artSlots ?? {})) {
    if (artId && !ART_IDS.has(artId)) errors.push(`Unknown curated art piece: ${artId}.`);
  }

  const ids = new Set<string>();
  const partsById = new Map<string, TablePart>();
  for (const part of table.parts) {
    if (!part.id.trim()) errors.push('Every table part needs an id.');
    if (ids.has(part.id)) errors.push(`Duplicate part id: ${part.id}.`);
    ids.add(part.id);
    partsById.set(part.id, part);
    if (!PLAYABLE_PART_KINDS.has(part.kind)) errors.push(`${part.id || 'Table part'} uses an unsupported gameplay mechanism: ${String(part.kind)}.`);
    if (!Number.isFinite(part.scoreValue) || part.scoreValue < 0) errors.push(`${part.id || part.kind} has an invalid score value.`);
    if (isRemoteOrEmbeddedUrl(part.soundRef) || isRemoteOrEmbeddedUrl(part.artRef)) errors.push(`${part.id} uses an unsafe remote or embedded asset reference.`);
    if (part.soundRef && !SOUND_EFFECT_IDS.has(part.soundRef as (typeof SOUND_EFFECTS)[number]['id'])) errors.push(`${part.id} uses an unknown curated sound effect.`);
    if (part.artRef && !ART_IDS.has(part.artRef)) errors.push(`${part.id} uses an unknown curated art piece.`);

    const points = pointsForPart(part);
    if (points.length > MAX_POINTS_PER_PART) {
      errors.push(`${part.id || part.kind} may define at most ${MAX_POINTS_PER_PART} geometry points.`);
    } else {
      for (const point of points) {
        if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) {
          errors.push(`${part.id} has a non-finite coordinate.`);
          break;
        }
        if (point.x < 0 || point.x > width || point.y < 0 || point.y > height) {
          errors.push(`${part.id} is outside the board bounds.`);
          break;
        }
      }
    }

    if ((part.kind === 'wall' || part.kind === 'guide-rail') && part.points.length < 2) errors.push(`${part.id} needs at least two points.`);
    if ((part.kind === 'wall' || part.kind === 'guide-rail') && !positiveFiniteAtMost(part.thickness, maxBoardSpan)) errors.push(`${part.id} needs a positive finite thickness.`);
    if (part.kind === 'slingshot' && part.points.length < 3) errors.push(`${part.id} needs at least three points.`);
    if (part.kind === 'ramp' && part.path.length < 2) errors.push(`${part.id} ramp path needs at least two points.`);
    if (part.kind === 'ramp' && part.path.length >= 2 && !pointsApproximatelyEqual(part.entry, part.path[0]!)) errors.push(`${part.id} ramp path must start at its entry.`);
    if (part.kind === 'ramp' && part.path.length >= 2 && !pointsApproximatelyEqual(part.exit, part.path.at(-1)!)) errors.push(`${part.id} ramp path must end at its exit.`);
    if (part.kind === 'ramp' && part.style !== undefined && part.style !== 'solid' && part.style !== 'wireform') errors.push(`${part.id} uses an unsupported ramp construction.`);
    if (part.kind === 'ramp' && part.transport !== undefined && part.transport !== 'orbit' && part.transport !== 'ramp') errors.push(`${part.id} uses an unsupported ramp transport.`);
    if (part.kind === 'ramp' && part.travelSeconds !== undefined && (!Number.isFinite(part.travelSeconds) || part.travelSeconds<.4 || part.travelSeconds>4)) errors.push(`${part.id} ride duration must be between 0.4 and 4 seconds.`);
    if (part.kind === 'ramp' && part.width !== undefined && (!Number.isFinite(part.width) || part.width < MIN_RAMP_WIDTH || part.width > MAX_RAMP_WIDTH)) errors.push(`${part.id} ramp width must be between ${MIN_RAMP_WIDTH} and ${MAX_RAMP_WIDTH} units.`);
    if (part.kind === 'ramp' && (!finiteMagnitudeAtMost(part.exitVelocity.x, MAX_SCRIPTED_VELOCITY_COMPONENT) || !finiteMagnitudeAtMost(part.exitVelocity.y, MAX_SCRIPTED_VELOCITY_COMPONENT))) errors.push(`${part.id} has an invalid ramp exit velocity.`);
    if ((part.kind === 'bumper' || part.kind === 'post' || part.kind === 'rollover') && !positiveFiniteAtMost(part.radius, maxBoardSpan)) errors.push(`${part.id} needs a positive radius.`);
    if (part.kind === 'bumper' && !nonNegativeFinite(part.impulse, MAX_BUMPER_IMPULSE)) errors.push(`${part.id} bumper impulse must be between 0 and ${MAX_BUMPER_IMPULSE}.`);
    if (part.kind === 'bumper' && part.scatterDeg !== undefined && !nonNegativeFinite(part.scatterDeg,6)) errors.push(`${part.id} bumper scatter must be between 0 and 6 degrees.`);
    if ((part.kind === 'flipper-left' || part.kind === 'flipper-right') && (!positiveFiniteAtMost(part.length, maxBoardSpan) || !Number.isFinite(part.restAngleDeg) || !positiveFiniteAtMost(part.sweepDeg, MAX_FLIPPER_SWEEP_DEG))) errors.push(`${part.id} has invalid flipper geometry.`);
    if (part.kind === 'plunger-lane' && (!positiveFiniteAtMost(part.bounds.width, width) || !positiveFiniteAtMost(part.bounds.height, height))) errors.push(`${part.id} has invalid shooter-lane bounds.`);
    if (part.kind === 'plunger-lane' && part.skillShotSpread !== undefined && !nonNegativeFinite(part.skillShotSpread,.5)) errors.push(`${part.id} skill-shot spread must be between 0 and 0.5.`);
    if (part.kind === 'plunger-lane' && part.divider && !positiveFiniteAtMost(part.divider.thickness, maxBoardSpan)) errors.push(`${part.id} divider needs a positive finite thickness.`);
    if (part.kind === 'plunger-lane' && part.stop && (!positiveFiniteAtMost(part.stop.width, width) || !positiveFiniteAtMost(part.stop.thickness, maxBoardSpan))) errors.push(`${part.id} has invalid shooter stop dimensions.`);
    if (part.kind === 'target' && !positiveFiniteAtMost(part.width, width)) errors.push(`${part.id} needs a positive target width.`);
    if (part.kind === 'warp' && !positiveFiniteAtMost(part.radius, maxBoardSpan)) errors.push(`${part.id} needs a positive warp radius.`);
    if (part.kind === 'spinner' && !positiveFiniteAtMost(part.width, width)) errors.push(`${part.id} needs a positive spinner width.`);
    if (part.kind === 'drain' && !positiveFiniteAtMost(part.width, width)) errors.push(`${part.id} needs a positive drain width.`);

    if (part.kind === 'insert') {
      if (!positiveFiniteAtMost(part.width, width) || !positiveFiniteAtMost(part.height, height)) errors.push(`${part.id} has invalid lamp dimensions.`);
      if (part.rotationDeg !== undefined && !Number.isFinite(part.rotationDeg)) errors.push(`${part.id} has an invalid lamp rotation.`);
      if (part.emissive !== undefined && !nonNegativeFinite(part.emissive, MAX_INSERT_EMISSIVE)) errors.push(`${part.id} lamp brightness must be between 0 and ${MAX_INSERT_EMISSIVE}.`);
    }

    if (part.kind === 'rollover' && !validOptionalBoost(part.scoreBoostSeconds, part.scoreBoostMultiplier)) errors.push(`${part.id} rollover boost must be 0–${MAX_BOOST_SECONDS} seconds and 1–${MAX_BOOST_MULTIPLIER}x.`);

    if (part.kind === 'kicker') {
      if (part.radius !== undefined && !positiveFiniteAtMost(part.radius, maxBoardSpan)) errors.push(`${part.id} needs a positive catch radius.`);
      if (!nonNegativeFinite(part.holdMs, MAX_KICKER_HOLD_MS)) errors.push(`${part.id} hold time must be between 0 and ${MAX_KICKER_HOLD_MS}ms.`);
      if (!finiteMagnitudeAtMost(part.ejectVelocity.x, MAX_SCRIPTED_VELOCITY_COMPONENT) || !finiteMagnitudeAtMost(part.ejectVelocity.y, MAX_SCRIPTED_VELOCITY_COMPONENT)) errors.push(`${part.id} has an invalid eject velocity.`);
      if (!validOptionalBoost(part.scoreBoostSeconds, part.scoreBoostMultiplier)) errors.push(`${part.id} catch boost must be 0–${MAX_BOOST_SECONDS} seconds and 1–${MAX_BOOST_MULTIPLIER}x.`);
    }

    if (part.kind === 'target') {
      if (part.creatureRef && !TARGET_PROPS.has(part.creatureRef)) errors.push(`${part.id} uses an unknown procedural target prop.`);
      if (part.hitPoints !== undefined && (!Number.isInteger(part.hitPoints) || part.hitPoints < 1 || part.hitPoints > 20)) errors.push(`${part.id} hit points must be an integer from 1–20.`);
      if (part.destroyScoreValue !== undefined && (!Number.isFinite(part.destroyScoreValue) || part.destroyScoreValue < 0)) errors.push(`${part.id} has an invalid destruction bonus.`);
      if (part.creatureRef && part.creatureRef !== 'none' && part.hitPoints === undefined) errors.push(`${part.id} needs hit points when a destructible prop is selected.`);
    }
  }

  for (const part of table.parts) {
    if (part.kind !== 'target' || !part.appearsAfterPartId) continue;
    if (part.appearsAfterPartId === part.id) errors.push(`${part.id} cannot trigger itself.`);
    if (!ids.has(part.appearsAfterPartId)) errors.push(`${part.id} references a missing appearance trigger.`);
  }

  if (table.rules) {
    if (table.rules.comboWindowMs !== undefined && !nonNegativeFinite(table.rules.comboWindowMs, MAX_COMBO_WINDOW_MS)) errors.push('Combo window must be between 0 and 60000 milliseconds.');
    if (table.rules.comboStepBonus !== undefined && !nonNegativeFinite(table.rules.comboStepBonus, MAX_RULE_BONUS)) errors.push('Combo step bonus must be a finite non-negative value.');
    if (table.rules.completionBonus !== undefined && !nonNegativeFinite(table.rules.completionBonus, MAX_RULE_BONUS)) errors.push('Completion bonus must be a finite non-negative value.');

    const missions = table.rules.missions ?? [];
    if (!Array.isArray(missions) || missions.length > MAX_MISSIONS) {
      errors.push(`A table may define at most ${MAX_MISSIONS} missions.`);
    } else {
      const missionIds = new Set<string>();
      for (const mission of missions) {
        const missionLabel = mission.id?.trim() || 'Mission';
        if (!mission.id?.trim()) errors.push('Every mission needs an id.');
        if (missionIds.has(mission.id)) errors.push(`Duplicate mission id: ${mission.id}.`);
        missionIds.add(mission.id);
        if (!mission.name?.trim()) errors.push(`${missionLabel} needs a name.`);
        if (!MISSION_TRIGGERS.has(mission.trigger)) errors.push(`${missionLabel} uses an unsupported trigger.`);
        if (!Number.isInteger(mission.count) || mission.count < 1 || mission.count > MAX_MISSION_COUNT) errors.push(`${missionLabel} count must be an integer from 1–${MAX_MISSION_COUNT}.`);
        if (!nonNegativeFinite(mission.bonus, MAX_RULE_BONUS)) errors.push(`${missionLabel} bonus must be a finite non-negative value.`);

        if (mission.partIds !== undefined) {
          if (!Array.isArray(mission.partIds) || mission.partIds.length === 0) {
            errors.push(`${missionLabel} part filter must contain at least one part id.`);
            continue;
          }
          const uniquePartIds = new Set(mission.partIds);
          if (uniquePartIds.size !== mission.partIds.length) errors.push(`${missionLabel} contains duplicate part ids.`);
          for (const partId of mission.partIds) {
            const part = partsById.get(partId);
            if (!part) {
              errors.push(`${missionLabel} references missing part: ${partId}.`);
              continue;
            }
            if (!missionPartMatchesTrigger(part, mission.trigger)) errors.push(`${missionLabel} part ${partId} does not match trigger ${mission.trigger}.`);
          }
        }
      }
    }
  }

  if (requirePlayableHardware) {
    if (table.parts.filter((part) => part.kind === 'flipper-left').length < 1) errors.push('A playable table needs at least one left flipper.');
    if (table.parts.filter((part) => part.kind === 'flipper-right').length < 1) errors.push('A playable table needs at least one right flipper.');
    if (table.parts.filter((part) => part.kind === 'drain').length !== 1) errors.push('A playable table needs exactly one drain.');
    if (table.parts.filter((part) => part.kind === 'plunger-lane').length !== 1) errors.push('A playable table needs exactly one plunger lane.');
  }

  return [...new Set(errors)];
}

export function validateTableDefinition(table: TableDefinition): string[] {
  return validateTable(table, true);
}

export function validateTableDraftDefinition(table: TableDefinition): string[] {
  return validateTable(table, false);
}

export function isPlayableTableDefinition(table: TableDefinition): boolean {
  return validateTableDefinition(table).length === 0;
}
